"use client";

import { useCallback, useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { CalendarClock } from "lucide-react";
import { cn } from "@/lib/cn";
import {
  DEFAULT_RANGE, RANGE_PRESETS, formatRangeLong, formatRangeShort,
  fromDateTimeLocal, parseDuration, toDateTimeLocal,
  type TimeRange,
} from "@/lib/timeRange";
import { useAnchoredPosition } from "./floating";
import { useIsClient } from "./hooks";
import { useEscapeStack } from "./overlayStack";
import { Button } from "./Button";
import { useTimeZonePreference } from "./Timestamp";

/* Phase 4.1 — TimeRangePicker.
 *
 * The most-used control on a SIEM's search, alerts and dashboard screens,
 * and until now it did not exist: ten raw <input type="date"> elements
 * across six files, with the preset list rebuilt in three of them. A bare
 * date input cannot express "the last 15 minutes", which is the window an
 * analyst spends most of their day in, and it drops the time component
 * entirely — so "today" meant midnight-to-midnight even mid-incident.
 *
 * ── Structure, after fal.ai's analytics range picker (Refero research) ──
 * That reference stacks three ways to say the same thing, cheapest first:
 * a free-text duration box, a grid of preset chips, then exact start/end
 * fields, with one dark Apply button committing the lot. The ordering is
 * the insight — someone who knows they want six hours types "6h" and is
 * done, someone who wants a common window clicks it, and only someone
 * pinning an exact evidence window pays the cost of the full form.
 *
 * Two deliberate departures from that reference:
 *
 *  - No calendar grid. fal.ai shows a month view; a SOC's absolute ranges
 *    are nearly always same-day or same-week and need a TIME, not just a
 *    date, so two <input type="datetime-local"> fields carry more of what
 *    is needed in far less space — and they bring the platform's own
 *    keyboard handling, locale formatting and screen-reader support, which
 *    a hand-rolled grid would have to reimplement and would get wrong.
 *
 *  - Relative vs absolute is shown, not just stored. A relative range is
 *    live: re-running the query moves the window. The panel says so, and
 *    the trigger marks it, because an analyst who does not notice they are
 *    pinned to a stale absolute window will read an incident as over when
 *    it is not. See lib/timeRange.ts for the model.
 *
 * Presets apply immediately (one click, no confirm — that is the fast path
 * and it should stay fast). Only the absolute form needs Apply, because a
 * half-typed timestamp must not re-query on every keystroke.
 */

/* d2d-XDR eval F18: the exact-window fields showed wall time with no zone,
   and analysts correlating with a UTC log line had to convert in their head.
   A Local/UTC toggle decides how the two fields are READ and WRITTEN; the
   emitted range is ISO either way. (lib/timeRange.ts is shared byte-for-byte
   with the upstream repos, so the UTC conversion lives here.) */
type FieldZone = "local" | "utc";
const localZoneName = (() => {
  try { return Intl.DateTimeFormat().resolvedOptions().timeZone || "Local"; } catch { return "Local"; }
})();
function toField(iso: string, zone: FieldZone): string {
  if (zone === "local") return toDateTimeLocal(iso);
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : d.toISOString().slice(0, 16);
}
function fromField(v: string, zone: FieldZone): string {
  if (zone === "local") return fromDateTimeLocal(v);
  if (!v) return "";
  const d = new Date(`${v}:00Z`);
  return Number.isNaN(d.getTime()) ? "" : d.toISOString();
}

export interface TimeRangePickerProps {
  value: TimeRange;
  onChange: (next: TimeRange) => void;
  /** Accessible name; also the panel's heading. */
  label?: string;
  /** Offer "All time". Off by default — an unbounded SIEM query is expensive. */
  allowAll?: boolean;
  size?: "sm" | "md";
  className?: string;
  disabled?: boolean;
}

export function TimeRangePicker({
  value,
  onChange,
  label = "Time range",
  allowAll = false,
  size = "md",
  className,
  disabled,
}: TimeRangePickerProps) {
  const id = useId();
  const panelId = `${id}-panel`;
  const [open, setOpen] = useState(false);
  const [quick, setQuick] = useState("");
  const [quickError, setQuickError] = useState(false);
  const [draftFrom, setDraftFrom] = useState("");
  const [draftTo, setDraftTo] = useState("");
  // The console-wide Local/UTC preference (ds/Timestamp), so the window you
  // type and every timestamp in the results are read in the same zone.
  const [zone, setZone] = useTimeZonePreference();

  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const mounted = useIsClient();

  const pos = useAnchoredPosition({ open, anchorRef: triggerRef, panelRef, side: "bottom", align: "end", offset: 6 });

  useEscapeStack(open, () => {
    setOpen(false);
    triggerRef.current?.focus();
  });

  /* Seeds the absolute fields from whatever is currently selected, so
     opening the panel on a pinned window starts from that window rather
     than from blank. Done in the OPEN handler rather than an effect: the
     click is the event that should cause it, and an effect would also
     re-run whenever `value` changed while the panel was open — overwriting
     what the analyst had half-typed into the From field. */
  const openPanel = useCallback(() => {
    if (value.kind === "absolute") {
      setDraftFrom(toField(value.from, zone));
      setDraftTo(toField(value.to, zone));
    }
    setQuick("");
    setQuickError(false);
    setOpen(true);
  }, [value, zone]);

  /* d2d-XDR eval F18: focus moves INTO the panel on open. It is a dialog;
     leaving focus on the trigger meant keyboard users had to find their way
     into a portalled panel that sits at the end of the document. The
     duration field is first: it is the fastest path. */
  useEffect(() => {
    if (!open || !pos.ready) return;
    panelRef.current?.querySelector<HTMLInputElement>("input")?.focus();
  }, [open, pos.ready]);

  /* Switching zone re-expresses the same instants in the other zone, so a
     half-typed window keeps meaning what it meant. */
  const switchZone = useCallback((next: FieldZone) => {
    setDraftFrom((v) => toField(fromField(v, zone), next));
    setDraftTo((v) => toField(fromField(v, zone), next));
    setZone(next);
  }, [zone]);

  useEffect(() => {
    if (!open) return;
    function onDocMouseDown(e: MouseEvent) {
      const t = e.target as Node;
      if (triggerRef.current?.contains(t) || panelRef.current?.contains(t)) return;
      setOpen(false);
    }
    document.addEventListener("mousedown", onDocMouseDown);
    return () => document.removeEventListener("mousedown", onDocMouseDown);
  }, [open]);

  const choose = useCallback((next: TimeRange) => {
    onChange(next);
    setOpen(false);
    triggerRef.current?.focus();
  }, [onChange]);

  const applyQuick = useCallback(() => {
    const parsed = parseDuration(quick);
    if (!parsed) { setQuickError(true); return; }
    choose(parsed);
  }, [quick, choose]);

  // Both bounds may legitimately be open: "everything after 09:00" is a real
  // question. But when both are set, From must come before To (eval F18) —
  // an inverted window silently returned nothing.
  const fromIso = fromField(draftFrom, zone);
  const toIso = fromField(draftTo, zone);
  const inverted = Boolean(fromIso && toIso && fromIso >= toIso);

  const applyAbsolute = useCallback(() => {
    if (inverted) return;
    choose({ kind: "absolute", from: fromIso, to: toIso });
  }, [fromIso, toIso, inverted, choose]);

  const activePresetShort = value.kind === "relative" ? `${value.amount}${value.unit}` : null;

  /* Portalled for the same reason every other floating panel in this system
     is: the picker's natural home is a filter toolbar, and those sit inside
     cards and scroll containers that would clip it. */
  const panel = open && mounted ? createPortal(
    <div
      ref={panelRef}
      id={panelId}
      role="dialog"
      aria-label={label}
      className="rc-timerange__panel"
      data-open={pos.ready}
      style={{ left: pos.x, top: pos.y }}
    >
      <div className="rc-timerange__section">
        <label htmlFor={`${id}-quick`} className="rc-timerange__section-label">
          Type a duration
        </label>
        <div className="rc-timerange__quick">
          <input
            id={`${id}-quick`}
            className="rc-timerange__quick-input"
            placeholder="e.g. 15m, 6h, 3d"
            value={quick}
            aria-invalid={quickError || undefined}
            aria-describedby={quickError ? `${id}-quick-err` : undefined}
            onChange={(e) => { setQuick(e.target.value); setQuickError(false); }}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); applyQuick(); } }}
          />
          <Button size="sm" variant="secondary" onClick={applyQuick} disabled={!quick.trim()}>
            Set
          </Button>
        </div>
        {quickError ? (
          <span id={`${id}-quick-err`} className="rc-timerange__error" role="alert">
            Use a number and a unit — 15m, 6h or 3d.
          </span>
        ) : null}
      </div>

      <div className="rc-timerange__section">
        <span className="rc-timerange__section-label" id={`${id}-presets`}>Quick ranges</span>
        <div className="rc-timerange__presets" role="group" aria-labelledby={`${id}-presets`}>
          {RANGE_PRESETS.map((p) => {
            const selected = activePresetShort === p.short;
            return (
              <button
                key={p.short}
                type="button"
                className="rc-timerange__preset"
                data-selected={selected || undefined}
                aria-pressed={selected}
                onClick={() => choose(p.range)}
              >
                {p.short}
              </button>
            );
          })}
          {allowAll ? (
            <button
              type="button"
              className="rc-timerange__preset"
              data-selected={value.kind === "all" || undefined}
              aria-pressed={value.kind === "all"}
              onClick={() => choose({ kind: "all" })}
            >
              All
            </button>
          ) : null}
        </div>
      </div>

      <div className="rc-timerange__section">
        <div className="rc-timerange__section-head">
          <span className="rc-timerange__section-label">Exact window</span>
          <div className="rc-timerange__zone" role="group" aria-label="Time zone for the exact window">
            {(["local", "utc"] as const).map((z) => (
              <button
                key={z}
                type="button"
                className="rc-timerange__zone-opt"
                aria-pressed={zone === z}
                onClick={() => switchZone(z)}
              >
                {z === "local" ? "Local" : "UTC"}
              </button>
            ))}
          </div>
        </div>
        <div className="rc-timerange__absolute">
          <label className="rc-timerange__field">
            <span>From</span>
            <input
              type="datetime-local"
              value={draftFrom}
              aria-invalid={inverted || undefined}
              aria-describedby={inverted ? `${id}-abs-err` : `${id}-zone`}
              onChange={(e) => setDraftFrom(e.target.value)}
            />
          </label>
          <label className="rc-timerange__field">
            <span>To</span>
            <input
              type="datetime-local"
              value={draftTo}
              aria-invalid={inverted || undefined}
              aria-describedby={inverted ? `${id}-abs-err` : `${id}-zone`}
              onChange={(e) => setDraftTo(e.target.value)}
            />
          </label>
        </div>
        <span id={`${id}-zone`} className="rc-timerange__zone-note">
          Times in {zone === "utc" ? "UTC" : localZoneName}
        </span>
        {inverted ? (
          <span id={`${id}-abs-err`} className="rc-timerange__error" role="alert">
            From must be earlier than To.
          </span>
        ) : null}
        <div className="rc-timerange__apply">
          {/* Says which mode the CURRENT selection is in, because the two
              behave differently after you walk away from the screen. */}
          <span className="rc-timerange__mode">
            {value.kind === "relative"
              ? "Relative — follows the clock"
              : value.kind === "absolute"
                ? "Pinned — will not move"
                : "Unbounded"}
          </span>
          <Button size="sm" onClick={applyAbsolute} disabled={(!draftFrom && !draftTo) || inverted}>
            Apply window
          </Button>
        </div>
      </div>
    </div>,
    document.body
  ) : null;

  return (
    <div className={cn("rc-timerange", `rc-timerange--${size}`, className)}>
      <button
        ref={triggerRef}
        type="button"
        className="rc-timerange__trigger"
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={open ? panelId : undefined}
        // The visible text is an abbreviation ("24h"); the accessible name
        // has to be the whole sentence, or a screen-reader user hears "24h
        // button" with no idea what it controls.
        aria-label={`${label}: ${formatRangeLong(value)}`}
        disabled={disabled}
        onClick={() => (open ? setOpen(false) : openPanel())}
      >
        <CalendarClock size={size === "sm" ? 13 : 15} aria-hidden />
        <span className="rc-timerange__value">{formatRangeShort(value)}</span>
        {value.kind === "relative" ? (
          /* A live window gets a mark, a pinned one does not. Paired with
             the text in the panel, never colour alone. */
          <span className="rc-timerange__live" aria-hidden title="Live range" />
        ) : null}
      </button>
      {panel}
    </div>
  );
}

export { DEFAULT_RANGE };
export type { TimeRange };
